// ============================================================
// Handover pack export (Brief v2 · Task 8).
//
// One-click QC handover document for project close-out. Where the engine's
// existing exports cover a single table / field / station, this assembles the
// WHOLE project into one printable PDF (open-in-tab → "Save as PDF"):
//
//   1. Project summary + key metrics (first-time pass, open NCRs)
//   2. ITP completion by field
//   3. NCR register (every failed check, status, NCR ref)
//   4. Document register (approved documents, revisions)
//   5. RFI register
//   6. Signature block
//
// It reuses the engine's derivation helpers (qcCounts/fieldStats/punchItems/…)
// so the numbers match every other view — no re-derivation (per CLAUDE.md).
// ============================================================
import { eng } from "./engineApi";
import { useProjects } from "@/state/project";
import { logAudit } from "./audit";

const esc = (s: unknown): string =>
  String(s ?? "").replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c] as string));

function meta() {
  const proj = useProjects.getState().current();
  const D = eng().D;
  return {
    name: proj?.name || D.project.name,
    client: proj?.client || D.project.client,
    epc: proj?.epc || D.project.epc,
    itp: proj?.itp_id || "ITP-BAL · Rev B",
    location: proj?.location || D.project.location,
  };
}

export function exportHandoverPack(): void {
  const e = eng();
  const m = meta();
  const qc = e.qcCounts();
  const fields = e.fieldStats() as Array<Record<string, number | string>>;
  const punch = e.punchItems() as Array<Record<string, any>>;
  const ncrs = punch.filter((p) => p.ncr);
  const docs = (e.tddDocs || []) as Array<Record<string, any>>;
  const rfis = (e.rfiList ? e.rfiList() : Object.values(e.rfis || {})) as Array<Record<string, any>>;

  const today = new Date().toLocaleString(undefined, {
    day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
  const firstPass = qc.done ? Math.round((qc.pass / qc.done) * 100) : 0;
  const accepted = fields.reduce((a, f) => a + Number(f.tablesAccepted || 0), 0);
  const rejected = fields.reduce((a, f) => a + Number(f.tablesRejected || 0), 0);
  const openNcr = ncrs.filter((n) => !n.resolved).length;
  const approvedDocs = docs.filter((d) => d.status === "approved").length;

  const summaryCards = `
    <div><div class="k">Project</div><div class="v">${esc(m.name)}</div></div>
    <div><div class="k">Client</div><div class="v">${esc(m.client)}</div></div>
    <div><div class="k">EPC</div><div class="v">${esc(m.epc)}</div></div>
    <div><div class="k">ITP</div><div class="v">${esc(m.itp)}</div></div>
    <div><div class="k">Tables accepted</div><div class="v">${accepted}${rejected ? ` · ${rejected} rejected` : ""}</div></div>
    <div><div class="k">First-time pass rate</div><div class="v status">${firstPass}%</div></div>
    <div><div class="k">Open NCRs</div><div class="v">${openNcr} of ${ncrs.length}</div></div>
    <div><div class="k">Approved documents</div><div class="v">${approvedDocs} of ${docs.length}</div></div>`;

  const fieldRows = fields
    .map(
      (f) => `<tr>
        <td class="mono">${esc(f.id)}</td>
        <td class="n">${esc(f.tables)}</td>
        <td class="n">${esc(f.tablesAccepted)}</td>
        <td class="n">${esc(f.tablesRejected)}</td>
        <td class="n">${esc(f.ncr)}</td>
        <td class="n">${esc(f.progress)}%</td>
        <td class="n">${esc(f.rate)}%</td>
      </tr>`,
    )
    .join("");

  const ncrRows = ncrs.length
    ? ncrs
        .map(
          (n) => `<tr class="${n.resolved ? "pass" : "fail"}">
        <td class="mono">${esc(n.ncr)}</td>
        <td>${esc(n.loc)}</td>
        <td>${esc(n.cat)} · ${esc(n.item)}</td>
        <td class="res">${n.resolved ? "Closed" : "Open"}</td>
        <td class="note">${esc(n.comment || "")}</td>
      </tr>`,
        )
        .join("")
    : `<tr><td colspan="5" class="note">No NCRs raised.</td></tr>`;

  const docRows = docs.length
    ? docs
        .map(
          (d) => `<tr class="${d.status === "approved" ? "pass" : "pending"}">
        <td>${esc(d.cat)}</td>
        <td>${esc(d.name)}</td>
        <td class="mono">${esc(d.ref || "")}</td>
        <td class="mono">${esc(d.rev || "")}</td>
        <td class="res">${esc(d.status)}</td>
      </tr>`,
        )
        .join("")
    : `<tr><td colspan="5" class="note">No documents registered.</td></tr>`;

  const rfiRows = rfis.length
    ? rfis
        .map(
          (r) => `<tr>
        <td class="mono">${esc(r.id)}</td>
        <td>${esc(r.title || "")}</td>
        <td class="res">${esc(r.status || "")}</td>
        <td>${esc(r.assignedTo || "")}</td>
      </tr>`,
        )
        .join("")
    : `<tr><td colspan="4" class="note">No RFIs raised.</td></tr>`;

  const html = `<!doctype html><html><head><meta charset="utf-8"><title>${esc(m.name)} — Handover Pack</title>
  <style>
    @page{size:A4 portrait;margin:14mm}
    *{box-sizing:border-box}
    body{font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Helvetica,Arial,sans-serif;color:#13212E;font-size:10.5px;line-height:1.45;margin:0;padding:20px 24px;background:#fff}
    .header{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:3px solid #2C9B73;padding-bottom:10px;margin-bottom:14px}
    .brand{font-family:Georgia,serif;font-size:20px;font-weight:700;letter-spacing:.3px}
    .brand small{display:block;font-family:-apple-system,sans-serif;font-size:9px;font-weight:600;color:#5A6B7A;letter-spacing:1.4px;margin-top:2px}
    .doctitle{text-align:right}
    .doctitle h1{margin:0;font-family:Georgia,serif;font-size:15px;font-weight:600}
    .doctitle .meta{font-size:9.5px;color:#5A6B7A;margin-top:4px;font-family:'Courier New',monospace}
    .summary{display:grid;grid-template-columns:repeat(4,1fr);gap:12px;background:#F5F6F8;padding:11px 13px;border-radius:3px;margin-bottom:16px;border-left:3px solid #2C9B73}
    .summary .k{font-size:8.5px;text-transform:uppercase;letter-spacing:1px;color:#5A6B7A;font-weight:600;margin-bottom:2px}
    .summary .v{font-size:11.5px;font-weight:600}
    .summary .v.status{color:#2C9B73}
    h3{font-family:Georgia,serif;font-size:13px;margin:18px 0 6px;padding-bottom:4px;border-bottom:1px solid #D6DEE5}
    table{width:100%;border-collapse:collapse;margin-bottom:8px}
    thead th{background:#F5F6F8;font-size:9px;text-transform:uppercase;letter-spacing:.6px;color:#5A6B7A;font-weight:700;text-align:left;padding:6px 7px;border-bottom:1.5px solid #13212E}
    tbody td{padding:6px 7px;border-bottom:1px solid #E8ECEF;vertical-align:top}
    tbody tr.fail td.res{color:#B84545;font-weight:700}
    tbody tr.pass td.res{color:#2C9B73;font-weight:700}
    tbody tr.pending td.res{color:#637588}
    td.n{text-align:center;font-family:'Courier New',monospace;color:#637588}
    td.mono,.mono{font-family:'Courier New',monospace;font-size:9.5px}
    td.note{color:#5A6B7A;font-style:italic}
    .signatures{display:grid;grid-template-columns:1fr 1fr 1fr;gap:24px;margin-top:28px;padding-top:18px;border-top:1px solid #D6DEE5}
    .sigblock{border-top:1px solid #13212E;padding-top:6px;font-size:9.5px}
    .sigblock .role{font-size:8.5px;text-transform:uppercase;letter-spacing:1px;color:#5A6B7A;font-weight:600;margin-bottom:3px}
    .foot{margin-top:18px;padding-top:10px;border-top:1px solid #D6DEE5;font-size:9px;color:#5A6B7A;display:flex;justify-content:space-between}
    @media print{ .noprint{display:none} body{padding:0} tbody tr{page-break-inside:avoid} h3{page-break-after:avoid} }
    .actions{position:fixed;top:12px;right:12px;display:flex;gap:8px;z-index:100}
    .actions button{padding:8px 16px;background:#2C9B73;color:#fff;border:none;border-radius:3px;cursor:pointer;font-weight:600;font-size:12px;font-family:inherit}
    .actions button.alt{background:#fff;color:#13212E;border:1px solid #D6DEE5}
  </style></head>
  <body>
    <div class="actions noprint">
      <button onclick="window.print()">Save as PDF / Print</button>
      <button class="alt" onclick="window.close()">Close</button>
    </div>
    <div class="header">
      <div class="brand">GridProof<small>powered by TerraTrace · QC HANDOVER PACK</small></div>
      <div class="doctitle"><h1>Quality Control Handover</h1><div class="meta">${esc(m.location)}<br>Generated ${esc(today)}</div></div>
    </div>
    <div class="summary">${summaryCards}</div>

    <h3>ITP completion by field</h3>
    <table>
      <thead><tr><th>Field</th><th>Tables</th><th>Accepted</th><th>Rejected</th><th>NCRs</th><th>Progress</th><th>Pass rate</th></tr></thead>
      <tbody>${fieldRows}</tbody>
    </table>

    <h3>NCR register (${ncrs.length})</h3>
    <table>
      <thead><tr><th>NCR</th><th>Location</th><th>Check</th><th>Status</th><th>Notes</th></tr></thead>
      <tbody>${ncrRows}</tbody>
    </table>

    <h3>Document register (${docs.length})</h3>
    <table>
      <thead><tr><th>Category</th><th>Document</th><th>Ref</th><th>Rev</th><th>Status</th></tr></thead>
      <tbody>${docRows}</tbody>
    </table>

    <h3>RFI register (${rfis.length})</h3>
    <table>
      <thead><tr><th>RFI</th><th>Title</th><th>Status</th><th>Assigned</th></tr></thead>
      <tbody>${rfiRows}</tbody>
    </table>

    <div class="signatures">
      <div class="sigblock"><div class="role">QC Manager</div>&nbsp;<br><span style="color:#637588;font-size:9px">Signature / Date</span></div>
      <div class="sigblock"><div class="role">EPC representative</div>&nbsp;<br><span style="color:#637588;font-size:9px">Signature / Date</span></div>
      <div class="sigblock"><div class="role">Client / Owner</div>&nbsp;<br><span style="color:#637588;font-size:9px">Signature / Date</span></div>
    </div>
    <div class="foot"><span>${esc(m.name)} · Generated ${esc(today)}</span><span>GridProof · powered by TerraTrace · Confidential</span></div>
  </body></html>`;

  const w = window.open("", "_blank");
  if (!w) {
    try {
      eng().toast("Please allow pop-ups to export the handover pack", "warn");
    } catch {
      /* ignore */
    }
    return;
  }
  w.document.open();
  w.document.write(html);
  w.document.close();
  void logAudit("handover.export", "project", meta().name, { ncrs: ncrs.length, docs: docs.length });
  try {
    eng().toast("Handover pack opened in new tab — use Save as PDF", "success");
  } catch {
    /* ignore */
  }
}
